// src/components/SignIn.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import SessionConsentModal from './SessionConsentModal';

const SignIn = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({ 
    email: '', 
    password: '' 
  });
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    form: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [isForgotSubmitting, setIsForgotSubmitting] = useState(false);
  const [forgotMessage, setForgotMessage] = useState({ type: '', text: '' });
  const [sessionMessage, setSessionMessage] = useState('');
  
  // 🔥 NEW: Consent modal states
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [pendingSessions, setPendingSessions] = useState([]);
  const [consentLoading, setConsentLoading] = useState(false);
  
  const navigate = useNavigate();

  // Check for session expired messages from URL params
  useEffect(() => {
    const reason = searchParams.get('reason');
    const sessionExpired = searchParams.get('session_expired');
    
    if (reason === 'session_expired') {
      setSessionMessage('Your session expired. Please login again.');
    } else if (reason === 'legacy_token') {
      setSessionMessage('Please login again to continue with the latest security update.');
    } else if (reason === 'token_expired') {
      setSessionMessage('Your token expired. Please login again.');
    } else if (sessionExpired === 'logged_in_elsewhere') {
      setSessionMessage('You were logged out from another device. Please login again.');
    }
    
    // Clear any stale token version
    if (sessionExpired || reason) {
      localStorage.removeItem('tokenVersion');
      localStorage.removeItem('sessionId');
    }
  }, [searchParams]);

  const validateForm = () => {
    let valid = true;
    const newErrors = { email: '', password: '', form: '' };

    if (!formData.email) {
      newErrors.email = 'Email is required';
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      valid = false;
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
      valid = false;
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();

    if (!forgotEmail) {
      setForgotMessage({ type: 'error', text: 'Email is required' });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail)) {
      setForgotMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    setIsForgotSubmitting(true);
    setForgotMessage({ type: '', text: '' });

    try {
      const response = await api.post('/api/new-auth/forgot-password', {
        email: forgotEmail.trim().toLowerCase()
      });

      const resetUrl = response.data?.resetUrl;
      setForgotMessage({
        type: 'success',
        text: resetUrl
          ? 'Password reset link generated. Use the link shown in the server response during development.'
          : 'If the email exists, a password reset link has been sent.'
      });
    } catch (error) {
      setForgotMessage({
        type: 'error',
        text: error.response?.data?.message || 'Unable to process password reset request.'
      });
    } finally {
      setIsForgotSubmitting(false);
    }
  };

  // 🔥 NEW: Handle consent confirm
  const handleConsentConfirm = async () => {
    setConsentLoading(true);
    setErrors(prev => ({ ...prev, form: '' }));
    
    try {
      console.log('[SignIn] User provided consent. Proceeding with login.');
      await performLogin(true);
    } catch (error) {
      console.error('[SignIn] Login after consent error:', error);
      handleLoginError(error);
    } finally {
      setConsentLoading(false);
      setShowConsentModal(false);
      setIsSubmitting(false);
    }
  };

  // 🔥 NEW: Handle consent cancel
  const handleConsentCancel = () => {
    console.log('[SignIn] User cancelled login due to active sessions.');
    setShowConsentModal(false);
    setPendingSessions([]);
    setIsSubmitting(false);
    setErrors(prev => ({
      ...prev,
      form: 'Login cancelled. You can continue using your existing session.'
    }));
  };

  // 🔥 NEW: Perform login with consent
  const performLogin = async (forceLogin = false) => {
    const response = await api.post('/api/new-auth/login', {
      email: formData.email.trim().toLowerCase(),
      password: formData.password,
      forceLogin: forceLogin
    });
    
    console.log('[SignIn] Full login response:', response.data);

    const { token, user, redirectTo, tokenVersion, sessionId, sessionTerminated, terminatedCount } = response.data;
    const mustChangePassword = response.data?.mustChangePassword || false;
    const passwordChangeReason = response.data?.passwordChangeReason || '';
    const resolvedRole = user?.role || response.data?.role || '';
    const consentProvided = response.data?.consentProvided || false;
    
    if (sessionTerminated) {
      console.log(`[SignIn] ${terminatedCount} previous session(s) terminated with consent: ${consentProvided}`);
    }
    
    // Store token version and session ID
    if (tokenVersion !== undefined) {
      localStorage.setItem('tokenVersion', tokenVersion.toString());
    }
    
    if (sessionId) {
      localStorage.setItem('sessionId', sessionId);
    }
    
    localStorage.setItem('token', token);
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('userId', user.id);
    }
    if (resolvedRole) {
      localStorage.setItem('userRole', resolvedRole);
    }
    if (mustChangePassword) {
      localStorage.setItem('mustChangePassword', 'true');
      localStorage.setItem('passwordChangeReason', passwordChangeReason);
      localStorage.setItem('postPasswordChangeRoute', redirectTo || '/dashboard');
    } else {
      localStorage.removeItem('mustChangePassword');
      localStorage.removeItem('passwordChangeReason');
      localStorage.removeItem('postPasswordChangeRoute');
    }
    
    // Handle redirects - pass mustChangePassword
    handleRedirect(redirectTo, user, response.data, mustChangePassword);
  };

  // 🔥 NEW: Handle redirect - added mustChangePassword parameter
  const handleRedirect = (redirectTo, user, responseData, mustChangePassword) => {
    // Check if user is already registered under a tenant (for main domain)
    if (user?.tenantRoles && Array.isArray(user.tenantRoles) && user.tenantRoles.length > 0) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userId');
      localStorage.removeItem('userRole');
      localStorage.removeItem('tokenVersion');
      localStorage.removeItem('sessionId');
      setErrors(prev => ({
        ...prev,
        form: 'You are already registered under an institution and cannot apply again. Please contact your institution for access.'
      }));
      setIsSubmitting(false);
      return;
    }
    
    if (mustChangePassword) {
      console.log('[SignIn] Password change required, redirecting to gate');
      navigate('/change-password', {
        replace: true,
        state: { nextRoute: redirectTo || '/dashboard' }
      });
      return;
    }

    if (redirectTo) {
      console.log('[SignIn] Navigating to redirectTo:', redirectTo);
      if (user?.role === 'coe' && redirectTo === '/coe') {
        navigate('/coe', { state: { user, tenant: responseData.tenant } });
      } else {
        navigate(redirectTo);
      }
      return;
    }
    
    if (user?.role === 'coe') {
      console.log('[SignIn] Navigating to COE dashboard');
      navigate('/coe', { state: { user, tenant: responseData.tenant } });
      return;
    }
    
    if (user?.isSuperAdmin || user?.role === 'super_admin' || user?.role === 'superadmin') {
      console.log('[SignIn] Navigating to super-admin dashboard');
      navigate('/super-admin/dashboard');
      return;
    }
    
    if (user?.hasApplication) {
      console.log('[SignIn] Application exists, navigating to ApplicationStatus', { userId: user.id });
      navigate('/application-status', { state: { userId: user.id } });
    } else {
      console.log('[SignIn] No application found, navigating to ApplicationForm', { userId: user.id });
      navigate('/application-form', { state: { userId: user.id } });
    }
  };

  // 🔥 NEW: Handle login errors
  const handleLoginError = (error) => {
    if (error.response?.data?.code === 'ACTIVE_SESSIONS_EXIST') {
      const data = error.response.data;
      setPendingSessions(data.sessions || []);
      setShowConsentModal(true);
      return;
    }
    
    if (error.response?.data?.code === 'TOKEN_VERSION_MISMATCH') {
      setErrors(prev => ({
        ...prev,
        form: 'Your session has expired because you logged in from another device. Please login again.'
      }));
      localStorage.clear();
      return;
    }
    
    if (error.response?.data?.code === 'LEGACY_TOKEN') {
      setErrors(prev => ({
        ...prev,
        form: 'Please login again to continue with the latest security update.'
      }));
      localStorage.clear();
      return;
    }
    
    if (error.response?.data?.code === 'SESSION_INACTIVE') {
      setErrors(prev => ({
        ...prev,
        form: 'Your session has expired or was terminated. Please login again.'
      }));
      localStorage.clear();
      return;
    }
    
    let message = error.response?.data?.message || 'Invalid credentials. Please try again.';
    if (error.response?.status === 401) {
      message = 'Invalid email/password.';
    }
    setErrors(prev => ({
      ...prev,
      form: message
    }));
  };

  // 🔥 UPDATED: handleSubmit with consent flow
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors(prev => ({ ...prev, form: '' }));

    try {
      console.log('[SignIn] Starting login flow...');
      
      // ============================================
      // Step 1: Check for active sessions (Consent Flow)
      // ============================================
      console.log('[SignIn] Step 1: Checking for active sessions...');
      
      const sessionCheckResponse = await api.post('/api/new-auth/check-sessions', {
        email: formData.email.trim().toLowerCase()
      });
      
      const sessionData = sessionCheckResponse.data;
      
      if (sessionData.hasActiveSessions) {
        console.log(`[SignIn] Found ${sessionData.activeSessionCount} active session(s). Showing consent modal.`);
        
        setPendingSessions(sessionData.sessions);
        setShowConsentModal(true);
        setIsSubmitting(false);
        return;
      }
      
      console.log('[SignIn] No active sessions found. Proceeding with login.');
      
      // ============================================
      // Step 2: Perform login (no active sessions)
      // ============================================
      await performLogin(false);
      
    } catch (error) {
      console.error('[SignIn] Sign-in error:', error);
      handleLoginError(error);
    } finally {
      if (!showConsentModal) {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center shadow-lg">
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-8 w-8 text-white" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" 
              />
            </svg>
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Sign in to your account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Manage your educational institution or organization
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          
          {/* Session Message */}
          {sessionMessage && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm flex items-start gap-2">
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <span>{sessionMessage}</span>
            </div>
          )}

          {errors.form && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg 
                    className="h-5 w-5 text-red-400" 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 20 20" 
                    fill="currentColor"
                  >
                    <path 
                      fillRule="evenodd" 
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" 
                      clipRule="evenodd" 
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{errors.form}</p>
                </div>
              </div>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1 relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`block w-full px-4 py-3 border ${errors.email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'} rounded-md shadow-sm`}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`block w-full px-4 py-3 border ${errors.password ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'} rounded-md shadow-sm`}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.478 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Forgot your password?
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </>
                ) : 'Sign in'}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  Don't have an account?
                </span>
              </div>
            </div>

            <div className="mt-6">
              <a
                href="/signup"
                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Create new account
              </a>
            </div>
          </div>
        </div>
      </div>

      {showForgotPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Reset your password</h3>
                <p className="text-sm text-slate-500">We will send a reset link to your email address.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotMessage({ type: '', text: '' });
                  setForgotEmail('');
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <span className="sr-only">Close</span>
                ×
              </button>
            </div>

            <form onSubmit={handleForgotPasswordSubmit} className="space-y-4 px-6 py-5">
              <div>
                <label htmlFor="forgot-email" className="block text-sm font-medium text-slate-700">
                  Email address
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="mt-1 block w-full rounded-lg border border-slate-300 px-4 py-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="you@example.com"
                />
              </div>

              {forgotMessage.text && (
                <div className={`rounded-lg border p-3 text-sm ${
                  forgotMessage.type === 'error'
                    ? 'border-red-200 bg-red-50 text-red-700'
                    : 'border-emerald-200 bg-emerald-50 text-emerald-700'
                }`}>
                  {forgotMessage.text}
                </div>
              )}

              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotMessage({ type: '', text: '' });
                    setForgotEmail('');
                  }}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isForgotSubmitting}
                  className={`rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 ${
                    isForgotSubmitting ? 'cursor-not-allowed opacity-75' : ''
                  }`}
                >
                  {isForgotSubmitting ? 'Sending...' : 'Send reset link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 🔥 NEW: Consent Modal */}
      {showConsentModal && (
        <SessionConsentModal
          sessions={pendingSessions}
          onConfirm={handleConsentConfirm}
          onCancel={handleConsentCancel}
          loading={consentLoading}
        />
      )}
    </div>
  );
};

export default SignIn;